// geo.ts — IP → 国家码 (v3.17.4 任务详情 v2)。
//
// 策略: 私有/保留地址本地识别为 LAN (不查询); 公网 IP 走同源后端代理
// /api/geo (v3.19.43: 此前浏览器直连 http://ip-api.com, HTTPS 管理页被
// 混合内容策略整批拦截, 失败还被 localStorage 缓存 30 天 → '??' 不恢复)。
// 服务端缓存成功 30 天 / 失败 5 分钟, 全浏览器共享; 本地 localStorage 仍作
// 一级缓存, 但失败 ('??') 条目只信 5 分钟。查询失败静默降级为 '??' —
// 地理标注是锦上添花, 永不阻塞数据展示。

const CACHE_KEY = 'afd-geo-cache-v1'
const CACHE_TTL = 30 * 24 * 3600 * 1000
const FAIL_TTL = 5 * 60 * 1000
const BATCH_SIZE = 15

let cache: Record<string, { cc: string; at: number }> = {}
let cacheLoaded = false

function loadCache(): void {
  if (cacheLoaded) return
  cacheLoaded = true
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) cache = JSON.parse(raw)
  } catch {
    cache = {}
  }
}

function saveCache(): void {
  try {
    // 顺手清掉已过期的失败条目, 防止历史 '??' 永久占据 localStorage
    const now = Date.now()
    for (const ip of Object.keys(cache)) {
      if (Date.now() - cache[ip].at > (cache[ip].cc === '??' ? FAIL_TTL : CACHE_TTL)) {
        delete cache[ip]
      }
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  } catch {
    /* 配额满/隐私模式 — 缓存失效但不影响功能 */
  }
}

/** 私有/回环/链路本地 → 局域网 (无需查询) */
export function isPrivateIP(ip: string): boolean {
  if (!ip) return false
  if (ip.includes(':')) {
    const low = ip.toLowerCase()
    return low.startsWith('fc') || low.startsWith('fd') || low.startsWith('fe80')
  }
  const parts = ip.split('.')
  if (parts.length !== 4) return false
  const a = Number(parts[0])
  const b = Number(parts[1])
  if (a === 10 || a === 127 || a === 0) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  if (a === 192 && b === 168) return true
  if (a === 169 && b === 254) return true
  if (a === 100 && b >= 64 && b <= 127) return true // CGNAT
  return false
}

interface PendingWaiter {
  cc: string
  resolve: (cc: string) => void
}

const pending = new Map<string, Promise<string>>()
let queue: string[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

async function flushBatch(ips: string[], waiters: PendingWaiter[]): Promise<void> {
  const byIp = new Map<string, string>()
  try {
    // v3.19.45 修复: okJSON 把响应包成 {ok, data:{results}} — v3.19.43 首版
    // 直接读顶层 results, 永远 undefined → 所有公网 IP 全部 '??' (无痕窗口
    // 实测抓出)。这里按 okJSON 实际形状取 data.results。
    // v3.19.53: 8s 超时 — 此前无超时, 面板后端挂起时 pending 里的 Promise
    // 永不落定, 对应 IP 的徽章永久卡住且不再重试。
    const res = await fetch(`/api/geo?ips=${encodeURIComponent(ips.join(','))}`, {
      signal: AbortSignal.timeout(8_000),
    })
    if (res.ok) {
      const body = (await res.json()) as { data?: { results?: Record<string, string> } }
      for (const [ip, cc] of Object.entries(body.data?.results ?? {})) {
        if (cc) byIp.set(ip, cc)
      }
    }
  } catch {
    /* 离线/超时 → 全部 '??' */
  }
  const now = Date.now()
  ips.forEach((ip) => {
    const cc = byIp.get(ip) ?? '??'
    cache[ip] = { cc, at: now }
    for (const w of waiters) if (w.cc === ip) w.resolve(cc)
  })
  // v3.19.51: saveCache 移出循环 — 此前每批 15 个 IP 连写 15 次 localStorage
  saveCache()
}

/** 查询国家码。返回 'LAN' / 两字母国家码 / '??'。并发去重。 */
export function lookupCountry(ip: string): Promise<string> {
  if (!ip) return Promise.resolve('??')
  if (isPrivateIP(ip)) return Promise.resolve('LAN')
  loadCache()
  const hit = cache[ip]
  // v3.19.43: 失败 ('??') 条目只信 5 分钟 — 此前统一 30 天, HTTPS 时代的
  // 旧失败缓存永不重试 (BT 对端徽章全员 '??' 的根因之一)。
  if (hit && Date.now() - hit.at < (hit.cc === '??' ? FAIL_TTL : CACHE_TTL)) {
    return Promise.resolve(hit.cc)
  }

  const inflight = pending.get(ip)
  if (inflight) return inflight

  // v3.19.25 TDZ 修复: 此前 pending.set(ip, p) 写在 Promise 执行器里 —
  // 执行器同步运行, 那一刻 p 还在暂时性死区 ("Cannot access 'n' before
  // initialization")。该分支只有公网 IP 才会走到 (私有/环回提前走 LAN
  // 返回), v3.19.23 成员地址改广播公网 IP 后首次被踩中 → 集群页整页崩溃。
  // 修复: 执行器只注册 resolve, 一切引用 p 的登记移到构造完成之后。
  let resolveP!: (cc: string) => void
  const p = new Promise<string>((resolve) => {
    resolveP = resolve
  })
  pending.set(ip, p)
  queue.push(ip)
  // 额外 waiter 记录以便批量回填 (同一 IP 多个调用者共享 pending promise)
  queueWaiters.push({ cc: ip, resolve: resolveP })
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null
      const ips = Array.from(new Set(queue))
      const ws = queueWaiters.splice(0)
      queue = []
      void flushBatch(ips, ws)
    }, 120)
  }
  return p
}

const queueWaiters: PendingWaiter[] = []
